import React from "react";
import Image from "next/legacy/image";
import Marquee from "react-fast-marquee";
import { Box, Flex } from "@chakra-ui/react";
import { Section, type SectionProps } from "components/section";
import { type IntegrationSimple } from "lib/sanity";
import { Link } from "ui/link";
import { TextTitle, type TextTitleProps } from "ui/text";
import { Button } from "ui/button";
import { negativeHorizontalContainerPadding } from "ui/container";
import { integrationsCount } from "../../constants/integrations";

const groupDestinations = (destinations: IntegrationSimple[]): IntegrationSimple[][] => {
  const numberOfGroups = 3;
  const groupSize = Math.round(destinations.length / numberOfGroups);
  const groups: IntegrationSimple[][] = [];

  for (let index = 0; index < numberOfGroups; index++) {
    const start = index * groupSize;
    const end = Math.min(start + groupSize, destinations.length);

    groups.push(destinations.slice(start, end));
  }

  return groups;
};

type DestinationMarqueeProps = {
  /**
   * Section title.
   */
  title?: string;
  /**
   * Section description.
   */
  description?: string;
  /**
   * Max width for the title and description.
   * @default "640px"
   */
  textMaxW?: TextTitleProps["maxW"];
  /**
   * Destinations to display. Expects all released destinations from Sanity.
   */
  destinations: IntegrationSimple[];
  /**
   * Optional search params to add to the link to the integrations catalog.
   *
   * E.g., "category=Advertising";
   *
   * @default ""
   */
  ctaSearchParams?: string;
  /**
   * Optional override for the text shown on the CTA button.
   *
   * @default "See all integrations"
   */
  ctaText?: string;
} & SectionProps;

export function DestinationMarquee({
  title = `Connect to ${integrationsCount}+ tools`,
  description = "Send any data to any tool. Skip building and maintaining pipelines, uploading CSVs, and having data silos across marketing, sales, customer success, finance, and analytics.",
  textMaxW = "640px",
  destinations,
  ctaSearchParams = "", // eslint-disable-line unicorn/prevent-abbreviations
  ctaText = "See all integrations",
  ...props
}: DestinationMarqueeProps) {
  if (destinations.length === 0) {
    return null;
  }

  const groupedDestinations = groupDestinations(destinations);

  return (
    <Section fullWidth {...props}>
      <TextTitle
        align="center"
        size="lg"
        title={title}
        description={description}
        maxW={textMaxW}
        actions={
          <Button hasRightChevron href={`/integrations${ctaSearchParams}`}>
            {ctaText}
          </Button>
        }
      />

      <Box display={{ base: "none", md: "flex" }} flexDirection="column" gap="bigger" mt="huge" pos="relative">
        {groupedDestinations.map((group, groupIndex) => (
          // Need length + index in key to properly adjust speed if number of destinations is changed (e.g. in Storybook)
          // eslint-disable-next-line react/no-array-index-key
          <Marquee key={`${group.length}-${groupIndex}`} speed={16} gradientWidth="100px">
            {group
              .filter((destination) => Boolean(destination.iconUrl))
              .map((destination, destinationIndex) => (
                <Link
                  key={destination.slug}
                  aria-label={`Learn about our ${destination.name} integration.`}
                  href={`/integrations/destinations/${destination.slug}`}
                  overflow="visible"
                  transition="transform 0.1s ease-in-out"
                  // Keep this link as an easter egg. it will be distracting and unnecessary for screen readers given the primary CTA above.
                  tabIndex={-1}
                  _hover={{ transform: "scale(0.92)" }}
                >
                  <Box
                    width="48px"
                    height="48px"
                    pos="relative"
                    ml={destinationIndex === 0 ? `${groupIndex * 40}px` : 12}
                    mr={12}
                  >
                    <Image
                      src={`${destination.iconUrl}?w=120`}
                      alt={destination.name}
                      title={destination.name}
                      layout="fill"
                      objectFit="contain"
                    />
                  </Box>
                </Link>
              ))}
          </Marquee>
        ))}
      </Box>

      <Box
        display={{ base: "flex", md: "none" }}
        flexDirection="column"
        gap="big"
        mt="biggest"
        mx={negativeHorizontalContainerPadding}
        maxW="100vw"
        overflow="hidden"
        pos="relative"
      >
        {groupedDestinations.map((group, groupIndex) => (
          <Flex
            // eslint-disable-next-line react/no-array-index-key
            key={`group${groupIndex}`}
            w="100%"
            justify="center"
            mr={groupIndex % 2 === 0 ? "small" : 0}
            ml={groupIndex % 2 === 0 ? 0 : "bigger"}
          >
            {group
              .slice(0, 6)
              .filter((destination) => Boolean(destination.iconUrl))
              .map((destination) => (
                <Link
                  key={destination.slug}
                  aria-label={`Learn about our ${destination.name} integration.`}
                  href={`/integrations/destinations/${destination.slug}`}
                  flex={1}
                  mx="big"
                  // Keep this link as an easter egg. it will be distracting and unnecessary for screen readers given the primary CTA above.
                  tabIndex={-1}
                >
                  <Box key={destination.slug} boxSize="big" pos="relative">
                    <Image
                      src={`${destination.iconUrl}?w=70`}
                      alt={destination.name}
                      title={destination.name}
                      layout="fill"
                      objectFit="contain"
                    />
                  </Box>
                </Link>
              ))}
          </Flex>
        ))}
      </Box>
    </Section>
  );
}
